import dayjs from 'dayjs'
import { type IAdvertise } from '../../../../store/slices/advertisesSlice'
import { type IOffer } from '../../../../store/slices/offersSlice'
import { type User } from '../../../../store/slices/userSlice'
import { type IViewing } from '../../../../store/slices/viewingsSlice'
import { type PortalBitrixRequest } from '../../../../functions/shared/orders/portalBitrixRequests'
import { type AgentActivityCard, type AgentActivityTaskItem } from './agentActivity.types'
import { ava_blank } from '../../../../urls'

interface BuildAgentActivityArgs {
    allUsers: User[]
    viewings: IViewing[]
    offers: IOffer[]
    advertises: IAdvertise[]
    portalRequests: PortalBitrixRequest[]
    viewingRouteById: Record<number, string>
    offerRouteById: Record<number, string>
    advertiseRouteById: Record<number, string>
    portalRouteByOrderId: Record<number, string>
}

interface Bucket {
    user: User | null
    viewings: AgentActivityTaskItem[]
    offers: AgentActivityTaskItem[]
    advertises: AgentActivityTaskItem[]
    portalRequests: AgentActivityTaskItem[]
}

const normalize = (value: unknown): string => String(value ?? '').trim().toLowerCase()

const isAgentCategory = (value: unknown): boolean => normalize(value).includes('agent')

const toDisplayDate = (value: string | null | undefined): string => {
    if (value == null || value === '') return 'Not set'
    const parsed = dayjs(value)
    return parsed.isValid() ? parsed.format('DD MMM YYYY') : 'Not set'
}

const toDisplayDateTime = (value: string | null | undefined): string => {
    if (value == null || value === '') return 'No date'
    const parsed = dayjs(value)
    return parsed.isValid() ? parsed.format('DD MMM YYYY, HH:mm') : 'No date'
}

const splitUnitName = (name: string | null | undefined): { unitLabel: string, locationLabel: string } => {
    if (name == null || name === '') {
        return {
            unitLabel: 'Unit not set',
            locationLabel: 'Location not set'
        }
    }
    const separator = name.includes(' <-> ') ? ' <-> ' : '-'
    const [unitNumber, building = '', location = ''] = name.split(separator)
    return {
        unitLabel: [unitNumber, building].filter(Boolean).join(', '),
        locationLabel: location || 'Location not set'
    }
}

const getFullName = (user: User | null): string => {
    const firstName = String(user?.ContactInfo?.FirstName ?? '').trim()
    const familyName = String(user?.ContactInfo?.FamilyName ?? '').trim()
    const combined = `${firstName} ${familyName}`.trim()
    return combined || 'Unknown agent'
}

const createEmptyBucket = (user: User | null): Bucket => ({
    user,
    viewings: [],
    offers: [],
    advertises: [],
    portalRequests: []
})

const ensureBucket = (map: Map<number, Bucket>, agentId: number, user: User | null): Bucket => {
    const existing = map.get(agentId)
    if (existing != null) return existing
    const next = createEmptyBucket(user)
    map.set(agentId, next)
    return next
}

const sortItems = (items: AgentActivityTaskItem[]): AgentActivityTaskItem[] =>
    items
        .sort((left, right) => dayjs(right.createdAt).valueOf() - dayjs(left.createdAt).valueOf())

export function buildAgentActivity({
    allUsers,
    viewings,
    offers,
    advertises,
    portalRequests,
    viewingRouteById,
    offerRouteById,
    advertiseRouteById,
    portalRouteByOrderId
}: BuildAgentActivityArgs): AgentActivityCard[] {
    const usersMap = new Map<number, User>()
    allUsers.forEach((user) => {
        usersMap.set(Number(user.id), user)
    })

    const buckets = new Map<number, Bucket>()

    viewings.forEach((viewing) => {
        const agentId = Number(viewing?.attributes?.User?.data?.id)
        if (!Number.isFinite(agentId)) return
        const route = viewingRouteById[Number(viewing.id)]
        if (route == null || route === '') return
        const user = usersMap.get(agentId) ?? null
        const bucket = ensureBucket(buckets, agentId, user)
        const { unitLabel, locationLabel } = splitUnitName(viewing?.attributes?.Name)

        bucket.viewings.push({
            id: `viewing-${String(viewing.id)}`,
            category: 'viewings',
            title: unitLabel || 'Viewing',
            subtitle: locationLabel,
            status: viewing?.attributes?.Statuses?.[0] ?? 'No status',
            createdAt: viewing?.attributes?.Datetime ?? viewing?.attributes?.createdAt ?? '',
            route,
            raw: viewing
        })
    })

    offers.forEach((offer) => {
        const agentId = Number(offer?.attributes?.User?.data?.id)
        if (!Number.isFinite(agentId)) return
        const route = offerRouteById[Number(offer.id)]
        if (route == null || route === '') return
        const user = usersMap.get(agentId) ?? null
        const bucket = ensureBucket(buckets, agentId, user)
        const { unitLabel, locationLabel } = splitUnitName(offer?.attributes?.Name ?? undefined)
        const offerAmount = offer?.attributes?.Type?.[0]?.Offer

        bucket.offers.push({
            id: `offer-${String(offer.id)}`,
            category: 'offers',
            title: unitLabel || 'Offer',
            subtitle: offerAmount != null ? `${String(offerAmount)} AED` : locationLabel,
            status: offer?.attributes?.Statuses?.[0] ?? 'No status',
            createdAt: offer?.attributes?.createdAt ?? '',
            route,
            raw: offer
        })
    })

    advertises.forEach((advertise) => {
        const agentId = Number(advertise?.attributes?.User?.data?.id)
        if (!Number.isFinite(agentId)) return
        const route = advertiseRouteById[Number(advertise.id)]
        if (route == null || route === '') return
        const user = usersMap.get(agentId) ?? null
        const bucket = ensureBucket(buckets, agentId, user)
        const { unitLabel, locationLabel } = splitUnitName(advertise?.attributes?.Name ?? undefined)

        bucket.advertises.push({
            id: `advertise-${String(advertise.id)}`,
            category: 'adverts',
            title: unitLabel || 'Advert',
            subtitle: locationLabel,
            status: advertise?.attributes?.Statuses?.[0] ?? 'No status',
            createdAt: advertise?.attributes?.createdAt ?? '',
            route,
            raw: advertise
        })
    })

    portalRequests.forEach((request) => {
        const requesterId = Number(request?.requesterUserId)
        if (!Number.isFinite(requesterId) || !isAgentCategory(request?.requesterCategory)) return
        const route = portalRouteByOrderId[Number(request.orderId)]
        if (route == null || route === '') return
        const user = usersMap.get(requesterId) ?? null
        const bucket = ensureBucket(buckets, requesterId, user)

        bucket.portalRequests.push({
            id: `portal-${String(request.orderId)}`,
            category: 'portal_requests',
            title: request.unitLabel,
            subtitle: request.propertyLabel,
            status: request.statusLabel,
            createdAt: request.createdAt,
            route,
            raw: request
        })
    })

    return Array.from(buckets.entries())
        .map(([agentId, bucket]) => {
            const avatar = bucket.user?.Type?.[0]?.Photo ?? bucket.user?.ContactInfo?.Avatar ?? ava_blank
            const company = bucket.user?.Settings?.[0]?.Company ?? 'Not set'
            const dateOfBirth = toDisplayDate(bucket.user?.ContactInfo?.DateOfBirth ?? bucket.user?.Type?.[0]?.DateOfBirth)
            const registrationDate = toDisplayDate(bucket.user?.createdAt)

            const sections = [
                {
                    key: 'viewings' as const,
                    title: 'Viewings',
                    items: sortItems(bucket.viewings),
                    total: bucket.viewings.length
                },
                {
                    key: 'offers' as const,
                    title: 'Offers',
                    items: sortItems(bucket.offers),
                    total: bucket.offers.length
                },
                {
                    key: 'adverts' as const,
                    title: 'Adverts',
                    items: sortItems(bucket.advertises),
                    total: bucket.advertises.length
                },
                {
                    key: 'portal_requests' as const,
                    title: 'Portal Requests',
                    items: sortItems(bucket.portalRequests),
                    total: bucket.portalRequests.length
                }
            ].filter((section) => section.total > 0)

            const totalActivity = sections.reduce((acc, section) => acc + section.total, 0)

            return {
                agentId,
                avatar,
                fullName: getFullName(bucket.user),
                company: String(company),
                dateOfBirth,
                registrationDate,
                totalActivity,
                sections: sections.map((section) => ({
                    ...section,
                    items: section.items.map((item) => ({
                        ...item,
                        subtitle: item.subtitle,
                        status: `${item.status} • ${toDisplayDateTime(item.createdAt)}`
                    }))
                })),
                user: bucket.user
            }
        })
        .filter((entry) => entry.totalActivity > 0)
        .sort((left, right) => right.totalActivity - left.totalActivity)
}
